import React from 'react';

import {
  ModalField,
  ModalInput,
  ModalNote,
} from '../../../../../design-system/patterns/modal-form';
import { MoneyInput, Readout, WizardPanel, WizardSegmented } from '../../wizard';

/**
 * Step 3, price and quantity. The view. Pure.
 *
 * The two mode toggles were hand-rolled `.toggle-btn` pairs with an `.active`
 * class. They are the shared segmented control now, which is the right
 * component and also the right doctrine: a segmented control is a COMMITTED
 * CHOICE, so the chosen segment fills with INK. That is the one place ink
 * appears on this step. Everything the system is reporting back (the market
 * price, the fee, the buyer price) is a readout or a disabled field, never a
 * filled control.
 *
 * It is `WizardSegmented`, not the desktop `Segmented` directly, because the
 * desktop one is `inline-flex` and `nowrap` and overflows the panel on a 320px
 * phone. `wizard.jsx` carries the measurement and the handoff.
 *
 * Prices are Rands with an R affix. The affix is on the field, not in the
 * value, so the string that reaches `formData.price` is unchanged and the
 * multipart body the wizard posts is byte for byte what it was.
 *
 * The breakdown line is preserved verbatim, separators included, because it is
 * the sentence the seller checks their maths against and it is asserted on in
 * `PricingQuantityStep.test.jsx`.
 */
export function PricingQuantityStepView({
  basePrice,
  pricingMode,
  onPricingModeChange,
  feeFrame,
  onFeeFrameChange,
  markupPercent,
  onMarkupChange,
  priceFloor,
  onFloorChange,
  fixedValue,
  onFixedValueChange,
  platformFee,
  buyer,
  breakdown,
  quantity,
  onQuantityChange,
  boughtForPrice,
  onBoughtForPriceChange,
  errors = {},
  titleRef,
}) {
  return (
    <WizardPanel
      ref={titleRef}
      step={3}
      title="Price and quantity"
      sub="Price against the live market price for this printing in this condition. The platform fee is shown before you commit, so the figure you take home is never a surprise."
    >
      <Readout label="Market price" value={basePrice ? `R${basePrice}` : 'No price'} />

      <ModalField label="Pricing Mode">
        <WizardSegmented
          label="Pricing mode"
          value={pricingMode}
          onChange={onPricingModeChange}
          items={[
            { value: 'fixed', label: 'Fixed price' },
            { value: 'tracked', label: 'Market-tracked' },
          ]}
        />
      </ModalField>

      <ModalField label="Fee Frame">
        <WizardSegmented
          label="Fee frame"
          value={feeFrame}
          onChange={onFeeFrameChange}
          items={[
            { value: 'inclusive', label: 'Incl. fee' },
            { value: 'exclusive', label: 'Excl. fee' },
          ]}
        />
      </ModalField>

      {pricingMode === 'tracked' ? (
        <>
          <ModalField label="Markup %" error={errors.markup_percent}>
            <ModalInput
              inputMode="decimal"
              value={markupPercent}
              onChange={onMarkupChange}
            />
          </ModalField>
          <ModalField label="Price floor (R)">
            <MoneyInput value={priceFloor} placeholder="0.00" onChange={onFloorChange} />
          </ModalField>
        </>
      ) : (
        <ModalField label="Price (R)" error={errors.price}>
          <MoneyInput value={fixedValue} placeholder="0.00" onChange={onFixedValueChange} />
        </ModalField>
      )}

      <Readout label="Platform fee" value={`${platformFee} %`} />
      <Readout label="Buyer price" value={buyer != null ? `R${buyer.toFixed(2)}` : 'No price'} />

      <ModalField
        label="Quantity"
        hint="All copies must be in the same condition."
        error={errors.quantity}
      >
        <ModalInput type="number" min="1" value={quantity} onChange={onQuantityChange} />
      </ModalField>

      {pricingMode === 'tracked' && buyer === null ? (
        <p className="dsk-hint">No market price, so use a fixed price.</p>
      ) : (
        <p className="dsa-cl-breakdown">
          Buyer pays R{breakdown.buyer.toFixed(2)} · Platform fee R{breakdown.fee.toFixed(2)} · You
          receive R{breakdown.net.toFixed(2)}
        </p>
      )}

      <ModalField label="Bought for price" optional>
        <MoneyInput
          value={boughtForPrice}
          placeholder="0.00"
          onChange={onBoughtForPriceChange}
        />
      </ModalField>
      <ModalNote>
        Enter what you paid for this card so your profit and loss is accurate. Leaving it blank
        does not block the listing, it only makes that figure a guess.
      </ModalNote>
    </WizardPanel>
  );
}

export default PricingQuantityStepView;
